import React, { useState } from 'react';
import type { OjtSession, OjtHandshakeConfig } from '../../../types';
import { useTranslation } from 'react-i18next';
import { SignaturePad } from './SignOffPrimitives';

interface SessionSignOffProps {
  session: OjtSession;
  handshakeConfig?: OjtHandshakeConfig;
  onComplete: (candidateSignature: string, leadHandSignature?: string) => void;
}

type SignOffStep = 'lead-hand' | 'candidate';

/**
 * Final session sign-off — first the lead hand signs to attest the session, then the candidate.
 * If no handshake method was configured for the block, the lead-hand step is skipped.
 */
export const SessionSignOff: React.FC<SessionSignOffProps> = ({ session, handshakeConfig, onComplete }) => {
  const { t } = useTranslation();
  const requiresLeadHandSignOff = handshakeConfig && (handshakeConfig.pinEnabled || handshakeConfig.selfieEnabled || handshakeConfig.qrEnabled);

  const [signOffStep, setSignOffStep] = useState<SignOffStep>(requiresLeadHandSignOff ? 'lead-hand' : 'candidate');
  const [leadHandSignatureData, setLeadHandSignatureData] = useState<string>('');

  const hoursLogged = session.startTime
    ? ((Date.now() - session.startTime) / 3600000).toFixed(1)
    : '0';

  if (signOffStep === 'lead-hand') {
    return (
      <div className="p-4 max-w-2xl mx-auto space-y-4">
        <div className="bg-white rounded-xl border border-aero-200 p-5">
          <h2 className="text-lg font-bold text-aero-900 mb-1">Lead Hand Sign-Off</h2>
          <p className="text-sm text-aero-500">Pass the device to the lead hand to sign off on this session.</p>
        </div>

        <div className="bg-aero-50 rounded-xl border border-aero-100 p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-aero-500">Lead Hand</span>
            <span className="font-medium text-aero-900">{session.leadHandName}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-aero-500">{t('candidate.ojt.session.hoursLogged')}</span>
            <span className="font-medium text-aero-900">{hoursLogged}h</span>
          </div>
        </div>

        <SignaturePad
          title="Lead Hand Signature"
          hint="Lead hand signs above"
          submitLabel="Confirm Lead Hand Sign-Off"
          onComplete={(sig) => {
            setLeadHandSignatureData(sig);
            setSignOffStep('candidate');
          }}
        />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      <div className="bg-white rounded-xl border border-aero-200 p-5">
        <h2 className="text-lg font-bold text-aero-900 mb-1">{t('candidate.ojt.signOff.title')}</h2>
        <p className="text-sm text-aero-500">{t('candidate.ojt.signOff.desc')}</p>
      </div>

      <div className="bg-aero-50 rounded-xl border border-aero-100 p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-aero-500">{t('candidate.ojt.session.started')}</span>
          <span className="font-medium text-aero-900">{session.startTime ? new Date(session.startTime).toLocaleTimeString() : '—'}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-aero-500">{t('candidate.ojt.session.hoursLogged')}</span>
          <span className="font-medium text-aero-900">{hoursLogged}h</span>
        </div>
      </div>

      <SignaturePad
        title={t('candidate.ojt.signOff.signature')}
        hint="Sign above"
        submitLabel={t('candidate.ojt.signOff.complete')}
        onComplete={(candidateSig) => onComplete(candidateSig, leadHandSignatureData || undefined)}
      />
    </div>
  );
};
